import Splide from '@splidejs/splide';

// ── Helpers : URL d'embed YouTube / Vimeo ─────────────────────────────────
const getYoutubeId = (url) => {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1] : null;
};

const getVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(?:.*\/)?(\d+)/);
  return match ? match[1] : null;
};

const buildEmbedUrl = (url, { autoplay = false, loop = false, mute = false, controls } = {}) => {
  const showControls = controls === undefined ? !autoplay : controls;

  const youtubeId = getYoutubeId(url);
  if (youtubeId) {
    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      mute: mute ? '1' : '0',
      loop: loop ? '1' : '0',
      controls: showControls ? '1' : '0',
      playsinline: '1',
      rel: '0',
      // Requis pour pouvoir forcer le mute via postMessage (cf. muteIframe) :
      // le paramètre "mute" seul n'est pas fiable sur tous les navigateurs.
      enablejsapi: '1',
    });
    if (!showControls) {
      // Masque le titre / la marque YouTube et les vidéos suggérées au survol.
      params.set('modestbranding', '1');
      params.set('iv_load_policy', '3');
      params.set('disablekb', '1');
    }
    if (loop) params.set('playlist', youtubeId);
    return `https://www.youtube.com/embed/${youtubeId}?${params.toString()}`;
  }

  const vimeoId = getVimeoId(url);
  if (vimeoId) {
    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      muted: mute ? '1' : '0',
      loop: loop ? '1' : '0',
      controls: showControls ? '1' : '0',
      playsinline: '1',
    });
    if (!showControls) {
      // Masque le titre, l'auteur et la miniature de profil Vimeo.
      params.set('title', '0');
      params.set('byline', '0');
      params.set('portrait', '0');
    }
    return `https://player.vimeo.com/video/${vimeoId}?${params.toString()}`;
  }

  return null;
};

// Coupe/active le son via l'API postMessage du player (YouTube / Vimeo).
const setIframeMuted = (iframe, url, muted) => {
  if (getYoutubeId(url)) {
    iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: muted ? 'mute' : 'unMute', args: [] }), '*');
  } else if (getVimeoId(url)) {
    iframe.contentWindow?.postMessage(JSON.stringify({ method: 'setVolume', value: muted ? 0 : 100 }), '*');
  }
};

// Force le mute au chargement via l'API postMessage : le paramètre
// "mute"/"muted" de l'URL n'est pas toujours respecté, surtout lorsque
// l'iframe est (re)créée suite à un geste utilisateur (ex: clic sur la
// flèche "suivant"), ce qui peut provoquer un bref passage en son audible.
const muteIframe = (iframe, url) => {
  if (!getYoutubeId(url) && !getVimeoId(url)) return;
  iframe.addEventListener('load', () => setIframeMuted(iframe, url, true));
};

// Affiche/masque l'overlay sombre (mode full) d'une slide.
const setSlideOverlayHidden = (slideEl, hidden) => {
  const overlay = slideEl.querySelector('.HeroSlider-overlay');
  overlay?.classList.toggle('-hidden', hidden);
};

const createIframe = (src) => {
  const iframe = document.createElement('iframe');
  iframe.src = src;
  iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
  iframe.setAttribute('allowfullscreen', '');
  iframe.setAttribute('frameborder', '0');
  return iframe;
};

// Charge (ou recharge) l'iframe autoplay d'une vidéo URL, et réinitialise
// le bouton mute associé (l'iframe redémarre toujours en muet).
const loadAutoplayEmbed = (embed) => {
  const url = embed.dataset.videoUrl;
  if (!url || embed.dataset.loaded) return;

  const controls = embed.dataset.videoControls === '1';
  const src = buildEmbedUrl(url, { autoplay: true, loop: true, mute: true, controls });
  if (!src) return;

  const iframe = createIframe(src);
  muteIframe(iframe, url);
  embed.appendChild(iframe);
  embed.dataset.loaded = '1';

  embed.style.opacity = '1';
  if (controls) embed.style.pointerEvents = 'auto';

  const slideEl = embed.closest('.HeroSlider-slide');

  if (controls) {
    if (slideEl) setSlideOverlayHidden(slideEl, true);
  } else {
    // YouTube/Vimeo affichent une interface de lancement (titre, lecture/
    // pause, précédent/suivant...) pendant quelques secondes après le
    // chargement, même avec controls=0. On masque l'iframe jusqu'à ce
    // qu'elle ait eu le temps de disparaître, puis on estompe l'overlay
    // au même moment.
    iframe.addEventListener('load', () => {
      setTimeout(() => {
        iframe.style.opacity = '1';
        if (slideEl) setSlideOverlayHidden(slideEl, true);
      }, 5000);
    });
  }

  const muteBtn = embed.parentElement?.querySelector('.HeroSlider-muteBtn');
  if (muteBtn) muteBtn.dataset.muted = '1';
};

// ── Coupe toute lecture audio/vidéo en cours dans une slide ────────────────
const pauseSlideMedia = (slideEl) => {
  // Vidéos fichier en autoplay (autoplay / muted_autoplay)
  slideEl.querySelectorAll('.HeroSlider-video').forEach((video) => {
    video.pause();
  });

  // Vidéos URL (YouTube / Vimeo) en autoplay
  slideEl.querySelectorAll('.HeroSlider-videoEmbed[data-video-autoplay="1"]').forEach((embed) => {
    const iframe = embed.querySelector('iframe');
    if (iframe) {
      iframe.remove();
      embed.dataset.loaded = '';
      embed.style.opacity = '0';
      embed.style.pointerEvents = 'none';
    }
  });

  // Aperçu image + lecture au clic
  slideEl.querySelectorAll('.HeroSlider-clickPlay').forEach((wrapper) => {
    wrapper._closeVideo?.();
  });

  setSlideOverlayHidden(slideEl, false);
};

// ── Relance la lecture autoplay d'une slide redevenue active ───────────────
const resumeSlideMedia = (slideEl) => {
  slideEl.querySelectorAll('.HeroSlider-video').forEach((video) => {
    video.play().catch(() => {});
    video.addEventListener('playing', () => setSlideOverlayHidden(slideEl, true), { once: true });
  });

  slideEl.querySelectorAll('.HeroSlider-videoEmbed[data-video-autoplay="1"]').forEach((embed) => {
    loadAutoplayEmbed(embed);
  });
};

const gk_heroSlider = {
  load() {
    const blocks = document.querySelectorAll('.HeroSliderBlock.splide');
    if (!blocks.length) return;

    blocks.forEach((el) => {
      // ── Splide ───────────────────────────────────────────────────────
      if (!el._splide) {
        const slideCount = el.querySelectorAll('.splide__slide').length;

        const effect    = el.dataset.effect || 'fade';
        const speed     = parseInt(el.dataset.speed, 10) || 750;
        const autoplay  = el.dataset.autoplay === '1';
        const interval  = parseInt(el.dataset.autoplayInterval, 10) || 5000;
        const rewind    = el.dataset.rewind === '1';
        const showArrows = el.dataset.arrowsPosition !== 'hidden';
        const showDots   = el.dataset.dotsPosition !== 'hidden';

        if (slideCount > 1) {
          const splide = new Splide(el, {
            type: effect === 'fade' ? 'fade' : 'slide',
            perPage: 1,
            speed,
            rewind,
            autoplay,
            interval,
            arrows: showArrows,
            pagination: showDots,
            easing: 'cubic-bezier(0.76, 0, 0.24, 1)',
            keyboard: 'global',
            i18n: {
              prev: 'Diapositive précédente',
              next: 'Diapositive suivante',
              first: 'Première diapositive',
              last: 'Dernière diapositive',
              slideX: 'Diapositive %s',
              pageX: 'Page %s',
            },
          });

          splide.mount();
          el._splide = splide;

          // Coupe la vidéo/audio de la slide quittée et relance celle de la slide active
          splide.on('move', (newIndex, prevIndex) => {
            const slideEls = el.querySelectorAll('.splide__slide');
            if (slideEls[prevIndex] && prevIndex !== newIndex) pauseSlideMedia(slideEls[prevIndex]);
            if (slideEls[newIndex]) resumeSlideMedia(slideEls[newIndex]);
          });
        }
      }

      // ── Vidéos fichier autoplay : masque l'overlay une fois la lecture lancée ──
      el.querySelectorAll('.HeroSlider-video').forEach((video) => {
        const slideEl = video.closest('.HeroSlider-slide');
        if (slideEl) video.addEventListener('playing', () => setSlideOverlayHidden(slideEl, true), { once: true });
      });

      // ── Vidéos autoplay (URL YouTube / Vimeo) ───────────────────────────
      el.querySelectorAll('.HeroSlider-videoEmbed[data-video-autoplay="1"]').forEach((embed) => {
        loadAutoplayEmbed(embed);
      });

      // ── Bouton mute/unmute (autoplay avec contrôles) ────────────────────
      el.querySelectorAll('.HeroSlider-muteBtn').forEach((btn) => {
        const media = btn.closest('.HeroSlider-media');
        const video = media?.querySelector('.HeroSlider-video');
        const embed = media?.querySelector('.HeroSlider-videoEmbed[data-video-autoplay="1"]');

        btn.addEventListener('click', () => {
          const wasMuted = btn.dataset.muted !== '0';
          const nextMuted = !wasMuted;

          if (video) {
            video.muted = nextMuted;
          }

          if (embed) {
            const iframe = embed.querySelector('iframe');
            const url = embed.dataset.videoUrl;
            if (iframe && url) setIframeMuted(iframe, url, nextMuted);
          }

          btn.dataset.muted = nextMuted ? '1' : '0';
        });
      });

      // ── Aperçu image + lecture au clic ──────────────────────────────────
      el.querySelectorAll('.HeroSlider-clickPlay').forEach((wrapper) => {
        const embed   = wrapper.querySelector('.HeroSlider-videoEmbed');
        const playBtn = wrapper.querySelector('.HeroSlider-playBtn');
        const closeBtn = wrapper.querySelector('.HeroSlider-closeBtn');

        if (!embed || !playBtn) return;

        const sourceType = wrapper.dataset.videoSourceType;
        const slideEl = wrapper.closest('.HeroSlider-slide');

        const playVideo = () => {
          if (!embed.dataset.loaded) {
            if (sourceType === 'file' && wrapper.dataset.videoSrc) {
              const video = document.createElement('video');
              video.src = wrapper.dataset.videoSrc;
              video.controls = true;
              video.autoplay = true;
              video.playsInline = true;
              embed.appendChild(video);
            } else if (sourceType === 'url' && wrapper.dataset.videoUrl) {
              const src = buildEmbedUrl(wrapper.dataset.videoUrl, { autoplay: true, loop: false, mute: false });
              if (src) embed.appendChild(createIframe(src));
            }
            embed.dataset.loaded = '1';
          } else {
            const video = embed.querySelector('video');
            if (video) video.play();
          }

          wrapper.classList.add('is-playing');
          if (slideEl) setSlideOverlayHidden(slideEl, true);
          if (el._splide) el._splide.Components.Autoplay?.pause();
        };

        const closeVideo = () => {
          wrapper.classList.remove('is-playing');
          if (slideEl) setSlideOverlayHidden(slideEl, false);

          const video = embed.querySelector('video');
          if (video) video.pause();

          const iframe = embed.querySelector('iframe');
          if (iframe) {
            // Stoppe la lecture en rechargeant l'iframe sans autoplay
            iframe.remove();
            embed.dataset.loaded = '';
          }

          if (el._splide) el._splide.Components.Autoplay?.play();
        };

        wrapper._closeVideo = closeVideo;

        playBtn.addEventListener('click', playVideo);
        closeBtn?.addEventListener('click', closeVideo);
      });
    });
  },
};

export default gk_heroSlider;
